import { ChevronsUpDown, Loader2 } from 'lucide-react'
import { Controller } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/modules/shared/components/ui/button'
import { Checkbox } from '@/modules/shared/components/ui/checkbox'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/modules/shared/components/ui/collapsible'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/modules/shared/components/ui/form'
import { Input } from '@/modules/shared/components/ui/input'
import { InputNumber } from '@/modules/shared/components/ui/input-number'
import { Label } from '@/modules/shared/components/ui/label'
import { cn } from '@/modules/shared/lib/utils'

import { useProductDetailsFormHook } from '../hooks/use-product-details-form.hook'

export function ProductDetailsFormComponent() {
	const {
		params,
		roundedQuantityMessage,
		isViewOrder,
		form,
		packageQuantity,
		handleSaveChanges,
		handleChangeDiscount,
		handleChangeUnitaryValue,
		accordionCommissionValue,
		accordionPercentageCommission,
		isCalculatingVolumes,
		product,
		quantityError,
		stockWarning,
		handleQuantityChange,
	} = useProductDetailsFormHook()

	const watchedQuantity = form.watch('quantity')

	// Manipuladores de eventos
	const handlePressEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleSaveChanges(form.getValues())
		}
	}

	const handleSelectAllText = (e: React.MouseEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement
		target.select()
	}

	// Funções para formatação de valores
	const formatPercentage = (value: string) => {
		let cleanValue = value.replace(/[^\d.,]/g, '')
		cleanValue = cleanValue.replace(',', '.')

		const parts = cleanValue.split('.')
		if (parts.length > 2) {
			cleanValue = parts[0] + '.' + parts.slice(1).join('')
		}
		if (parts[1] && parts[1].length > 4) {
			cleanValue = parts[0] + '.' + parts[1].substring(0, 4)
		}

		return cleanValue
	}

	const formatCurrency = (value: string) => {
		let cleanValue = value.replace(/[^\d.,]/g, '')
		cleanValue = cleanValue.replace(',', '.')

		const parts = cleanValue.split('.')
		if (parts.length > 2) {
			cleanValue = parts[0] + '.' + parts.slice(1).join('')
		}
		if (parts[1] && parts[1].length > 2) {
			cleanValue = parts[0] + '.' + parts[1].substring(0, 2)
		}

		return cleanValue
	}

	// Função para validar desconto máximo
	const validateMaxDiscount = (discountValue: number) => {
		if (!product?.porcMaxDesconto || product.porcMaxDesconto === 0) {
			// Se porcMaxDesconto for 0 ou não existir, pode digitar até 100%
			return discountValue <= 100
		}
		// Se porcMaxDesconto existe e é maior que 0, valida pelo limite
		return discountValue <= product.porcMaxDesconto
	}

	// Manipuladores de mudanças nos inputs - Passam o valor formatado para as funções
	const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formattedValue = formatPercentage(e.target.value)
		const discountNumber = Number(formattedValue.replace(',', '.'))

		if (discountNumber < 0) return

		// Validar desconto máximo
		if (!validateMaxDiscount(discountNumber)) {
			const maxDiscount = product?.porcMaxDesconto && product.porcMaxDesconto > 0 ? product.porcMaxDesconto : 100
			toast.info(`Desconto máximo permitido: ${maxDiscount}%`)
			return
		}

		form.setValue('discount', formattedValue)
		// Passa o valor formatado diretamente para a função de cálculo
		handleChangeDiscount(formattedValue)
	}

	const handleUnitaryValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formattedValue = formatCurrency(e.target.value)
		form.setValue('unitaryValue', formattedValue)
		// Passa o valor formatado diretamente para a função de cálculo
		handleChangeUnitaryValue(formattedValue)
	}

	// Novo manipulador para quantidade
	const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Math.max(0, Number(e.target.value) || 0)
		handleQuantityChange(value)
	}

	return (
		<Form {...form}>
			{/* Campos de Volumes e Quantidade */}
			{params.quantityParams?.visible !== false && (
				<>
					<div className="flex space-x-2">
						<FormField
							control={form.control}
							name="volumes"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Volumes</FormLabel>
									<FormControl>
										<InputNumber
											{...field}
											onKeyUp={handlePressEnter}
											disabled={
												isViewOrder ||
												(params.quantityParams &&
													!params.quantityParams?.enabled)
											}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="quantity"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Qtd. Produtos</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={
												Number.isFinite(field.value) && field.value % 1 !== 0 && field.value.toString().split('.')[1]?.length > 2
													? field.value.toFixed(2)
													: field.value
											}
											type="number"
											inputMode="numeric"
											min="0"
											disabled={
												isViewOrder ||
												(params.quantityParams &&
													!params.quantityParams?.enabled)
											}
											onFocus={(ev) => ev.currentTarget.select()}
											onChange={(e) => {
												const value = Math.max(0, Number(e.target.value) || 0)
												handleQuantityChange(value)
											}}
											onKeyUp={handlePressEnter}
											className={cn(
												quantityError && 'border-red-500 focus:border-red-500'
											)}
										/>
									</FormControl>
									{quantityError && (
										<p className="text-red-500 text-xs mt-1">
											Quantidade deve ser múltipla de {packageQuantity}
										</p>
									)}
								</FormItem>
							)}
						/>
					</div>
					{stockWarning && <p role="status" className="text-amber-700 text-sm mt-2">{stockWarning}</p>}
					{roundedQuantityMessage && (
						<p className="text-red-500 text-sm mt-2 font-semibold">
							{roundedQuantityMessage}
						</p>
					)}
				</>
			)}

			{/* Campos de Desconto e Valor Unitário */}
			<div className="flex space-x-2 mt-4">
				{params.discountParams?.visible !== false && (
					<FormField
						control={form.control}
						name="discount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Desconto %
									{product?.porcMaxDesconto && product.porcMaxDesconto > 0 && (
										<span className="text-xs text-gray-500 ml-1">
											(máx: {product.porcMaxDesconto}%)
										</span>
									)}
								</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											placeholder="0,00"
											disabled={
												isViewOrder ||
												(params.quantityParams && !params.quantityParams?.enabled) ||
												watchedQuantity <= 0
											}
											onClick={handleSelectAllText}
											onChange={handleDiscountChange}
											onKeyUp={handlePressEnter}
											className="pr-8"
										/>
										<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
											%
										</span>
									</div>
								</FormControl>
								<FormMessage className="text-xs" />
							</FormItem>
						)}
					/>
				)}

				{params.unitaryValueParams?.visible !== false && (
					<div>
						<FormField
							control={form.control}
							name="unitaryValue"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Valor unitário</FormLabel>
									<FormControl>
										<div className="relative">
											<Input
												{...field}
												placeholder="0,00"
												type="text"
												disabled={
													isViewOrder ||
													(params.quantityParams && !params.quantityParams?.enabled) ||
													watchedQuantity <= 0
												}
												onClick={handleSelectAllText}
												onChange={handleUnitaryValueChange}
												onKeyUp={handlePressEnter}
											/>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						{/* Checkbox de Valor Fixo/Negociado */}
						{params.fixedValueParams?.visible !== false && (
							<Controller
								control={form.control}
								name="fixedValue"
								defaultValue={false}
								render={({ field: { ref, value, onChange } }) => (
									<div className="flex items-center space-x-2 mt-2">
										<Checkbox
											ref={ref}
											checked={value}
											onChange={onChange}
											disabled={isViewOrder}
										/>
										<Label>Vr Negociado</Label>
									</div>
								)}
							/>
						)}
					</div>
				)}
			</div>

			{/* Seção de Comissão (Collapsible) */}
			{params.showCommissionParams && (
				<Collapsible className="mt-4">
					<div className="flex items-center justify-between space-x-4 ">
						<h4 className="text-sm font-semibold">Mais informações</h4>
						<CollapsibleTrigger asChild>
							<Button variant="ghost" size="sm" className="w-9 p-0">
								<ChevronsUpDown className="h-4 w-4" />
								<span className="sr-only">Mais informações</span>
							</Button>
						</CollapsibleTrigger>
					</div>
					<CollapsibleContent>
						<div className="text-sm p-2 bg-gray-50 rounded mt-2">
							<p>% Comissão: {accordionPercentageCommission}%</p>
							<p>Vr Comissão: {accordionCommissionValue}</p>
						</div>
					</CollapsibleContent>
				</Collapsible>
			)}

			{/* Botão de Salvar */}
			<Button
				className="w-full mt-4"
				disabled={isViewOrder || isCalculatingVolumes || quantityError}
				onClick={form.handleSubmit(handleSaveChanges)}
			>
				{isCalculatingVolumes && (
					<Loader2 className="animate-spin mr-2" size={16} />
				)}
				{isCalculatingVolumes ? 'Calculando...' : 'Salvar'}
			</Button>
		</Form>
	)
}
